import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useDarkMode } from './DarkModeContext';
import { Check } from 'react-feather';
import api from './api';
import Footer from './Footer';

function Status() {
    const { darkMode } = useDarkMode();
    const [healthData, setHealthData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastChecked, setLastChecked] = useState(null);
    const [hoveredDay, setHoveredDay] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

    const checkHealth = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.health();
            setHealthData(response.data);
            setLastChecked(new Date());
        } catch (err) {
            setError(err.message || 'Failed to fetch health status');
            setHealthData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        checkHealth();
        // Auto-refresh every 30 seconds
        const interval = setInterval(checkHealth, 30000);
        return () => clearInterval(interval);
    }, []);

    const formatUptime = (seconds) => {
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        
        if (days > 0) {
            return `${days}d ${hours}h ${minutes}m ${secs}s`;
        } else if (hours > 0) {
            return `${hours}h ${minutes}m ${secs}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        } else {
            return `${secs}s`;
        }
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    const getMongoStateText = (state) => {
        const states = {
            0: 'Disconnected',
            1: 'Connected',
            2: 'Connecting',
            3: 'Disconnecting'
        };
        return states[state] || `Unknown (${state})`;
    };

    // Generate 45 days of uptime data (all normal/100%)
    const generateUptimeData = () => {
        const days = [];
        const today = new Date();
        for (let i = 44; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            days.push({
                date,
                status: 'normal', // All days are normal for 100% uptime
                uptime: 100
            });
        }
        return days;
    };

    const uptimeData = generateUptimeData();

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };

    const handleBarHover = (e, day) => {
        setHoveredDay(day);
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPosition({
            x: rect.left + rect.width / 2,
            y: rect.top - 10
        });
    };

    const handleBarLeave = () => {
        setHoveredDay(null);
    };

    return (
        <>
            <Helmet>
                <title>System Status - Merra AI</title>
                <meta name="description" content="Check the health and status of Merra AI services. Monitor system uptime, database connectivity, and service availability." />
                <meta name="keywords" content="Merra AI status, system health, service status, Merra AI uptime" />
                <link rel="canonical" href="https://www.merra.ai/status" />
                <meta property="og:title" content="System Status - Merra AI" />
                <meta property="og:description" content="Check the health and status of Merra AI services." />
                <meta property="og:url" content="https://www.merra.ai/status" />
            </Helmet>
            <div style={{ 
                maxWidth: '800px', 
                margin: '0 auto', 
                padding: '20px',
                fontFamily: 'Arial, sans-serif',
                lineHeight: '1.6',
                color: darkMode ? '#e0e0e0' : '#333',
                backgroundColor: darkMode ? '#1a1a1a' : 'white',
                minHeight: '100vh'
            }}>
                <div style={{ marginBottom: '30px' }}>
                    <Link to="/" style={{ color: darkMode ? '#4A9EFF' : '#1976d2', textDecoration: 'none' }}>
                        ← Back to Home
                    </Link>
                </div>

                <h1 style={{ 
                    color: darkMode ? '#ffffff' : '#2c3e50', 
                    borderBottom: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`, 
                    paddingBottom: '10px',
                    textAlign: 'center',
                    marginBottom: '40px'
                }}>
                    System Status
                </h1>

                {loading && !healthData && (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px',
                        color: darkMode ? '#e0e0e0' : '#666'
                    }}>
                        <div style={{ fontSize: '18px', marginBottom: '10px' }}>Checking system status...</div>
                    </div>
                )}

                {error && (
                    <div style={{ 
                        backgroundColor: darkMode ? '#4a1a1a' : '#ffe6e6',
                        border: `1px solid ${darkMode ? '#ff6b6b' : '#ff9999'}`,
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '30px',
                        color: darkMode ? '#ff9999' : '#cc0000'
                    }}>
                        <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Error</h3>
                        <p style={{ margin: 0 }}>{error}</p>
                        <button
                            onClick={checkHealth}
                            style={{
                                marginTop: '15px',
                                padding: '10px 20px',
                                backgroundColor: darkMode ? '#AB5898' : '#AB5898',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {healthData && (
                    <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        marginBottom: '30px'
                    }}>
                        {/* Overall Status */}
                        <div style={{ 
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`,
                            borderRadius: '12px',
                            padding: '25px'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                marginBottom: '15px'
                            }}>
                                <h2 style={{ 
                                    margin: 0, 
                                    color: darkMode ? '#ffffff' : '#2c3e50',
                                    fontSize: '20px'
                                }}>
                                    Service Status
                                </h2>
                                <span style={{
                                    padding: '6px 12px',
                                    borderRadius: '20px',
                                    backgroundColor: healthData.status === 'ok' 
                                        ? (darkMode ? '#1a4d1a' : '#d4edda')
                                        : (darkMode ? '#4a1a1a' : '#f8d7da'),
                                    color: healthData.status === 'ok'
                                        ? (darkMode ? '#90ee90' : '#155724')
                                        : (darkMode ? '#ff9999' : '#721c24'),
                                    fontSize: '14px',
                                    fontWeight: 'bold'
                                }}>
                                    {healthData.status === 'ok' ? '✓ Operational' : '✗ Issues Detected'}
                                </span>
                            </div>
                        </div>

                        {/* Uptime */}
                        <div style={{ 
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`,
                            borderRadius: '12px',
                            padding: '25px'
                        }}>
                            <h3 style={{ 
                                margin: '0 0 15px 0', 
                                color: darkMode ? '#ffffff' : '#2c3e50',
                                fontSize: '18px'
                            }}>
                                Uptime
                            </h3>
                            <div style={{ 
                                fontSize: '24px',
                                fontWeight: 'bold',
                                color: darkMode ? '#4A9EFF' : '#1976d2'
                            }}>
                                {formatUptime(healthData.uptime)} 
                            <p style={{ 
                                fontSize: '14px',
                                color: darkMode ? '#b0b0b0' : '#666',
                                marginBottom: '10px',
                                fontWeight: 'normal'
                            }}>
                                Reset reason: code update
                            </p>
                            </div>
                        </div>

                        {/* Database Status */}
                        <div style={{ 
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`,
                            borderRadius: '12px',
                            padding: '25px'
                        }}>
                            <h3 style={{ 
                                margin: '0 0 15px 0', 
                                color: darkMode ? '#ffffff' : '#2c3e50',
                                fontSize: '18px'
                            }}>
                                Database Connection
                            </h3>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '10px',
                                marginBottom: '10px'
                            }}>
                                <span style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: healthData.mongo?.connected 
                                        ? (darkMode ? '#90ee90' : '#28a745')
                                        : (darkMode ? '#ff6b6b' : '#dc3545')
                                }}></span>
                                <span style={{
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    color: healthData.mongo?.connected
                                        ? (darkMode ? '#90ee90' : '#28a745')
                                        : (darkMode ? '#ff9999' : '#dc3545')
                                }}>
                                    {healthData.mongo?.connected ? 'Connected' : 'Disconnected'}
                                </span>
                            </div>
                            <div style={{ 
                                fontSize: '14px',
                                color: darkMode ? '#b0b0b0' : '#666',
                                marginTop: '5px'
                            }}>
                                State: {getMongoStateText(healthData.mongo?.state)}
                            </div>
                        </div>

                        {/* Timestamp */}
                        <div style={{ 
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`,
                            borderRadius: '12px',
                            padding: '25px'
                        }}>
                            <h3 style={{ 
                                margin: '0 0 15px 0', 
                                color: darkMode ? '#ffffff' : '#2c3e50',
                                fontSize: '18px'
                            }}>
                                Last Check
                            </h3>
                            <div style={{ 
                                fontSize: '16px',
                                color: darkMode ? '#b0b0b0' : '#666'
                            }}>
                                {lastChecked ? formatTimestamp(lastChecked.getTime()) : formatTimestamp(healthData.timestamp)}
                            </div>
                        </div>
                    </div>
                )}

                {/* Uptime Graph */}
                <div style={{ 
                    backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                    border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`,
                    borderRadius: '12px',
                    padding: '25px',
                    marginBottom: '30px',
                    position: 'relative'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px'
                    }}>
                        <h3 style={{ 
                            margin: 0, 
                            color: darkMode ? '#ffffff' : '#2c3e50',
                            fontSize: '18px'
                        }}>
                            merra.ai
                        </h3>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: darkMode ? '#90ee90' : '#28a745',
                            fontSize: '18px',
                            fontWeight: 'bold'
                        }}>
                            100%
                            <Check size={16} />
                        </div>
                    </div>
                    
                    <div style={{
                        position: 'relative',
                        marginBottom: '10px'
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: '2px',
                            alignItems: 'flex-end',
                            height: '60px',
                            position: 'relative'
                        }}>
                            {uptimeData.map((day, index) => (
                                <div
                                    key={index}
                                    onMouseEnter={(e) => handleBarHover(e, day)}
                                    onMouseLeave={handleBarLeave}
                                    style={{
                                        flex: 1,
                                        height: '100%',
                                        backgroundColor: darkMode ? '#28a745' : '#28a745',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'opacity 0.2s',
                                        minWidth: '4px'
                                    }}
                                />
                            ))}
                        </div>
                        
                        {/* Tooltip */}
                        {hoveredDay && (
                            <div
                                style={{
                                    position: 'fixed',
                                    left: `${tooltipPosition.x}px`,
                                    top: `${tooltipPosition.y}px`,
                                    transform: 'translate(-50%, -100%)',
                                    backgroundColor: darkMode ? '#333' : '#2c3e50',
                                    color: 'white',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    pointerEvents: 'none',
                                    zIndex: 1000,
                                    whiteSpace: 'nowrap',
                                    marginBottom: '5px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                }}
                            >
                                <div style={{ marginBottom: '4px' }}>
                                    {formatDate(hoveredDay.date)}
                                </div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    color: '#90ee90'
                                }}>
                                    <span style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: '#90ee90',
                                        display: 'inline-block'
                                    }}></span>
                                    Normal
                                </div>
                                <div style={{ marginTop: '4px', fontSize: '11px', opacity: 0.8 }}>
                                    24h
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '12px',
                        color: darkMode ? '#b0b0b0' : '#666',
                        marginTop: '10px'
                    }}>
                        <span>45 days ago</span>
                        <span>today</span>
                    </div>
                </div>

                {/* Refresh Button */}
                <div style={{ textAlign: 'center', marginBottom: '50px' }}>
                    <button
                        onClick={checkHealth}
                        disabled={loading}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: darkMode ? '#AB5898' : '#AB5898',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            opacity: loading ? 0.6 : 1,
                            transition: 'opacity 0.2s'
                        }}
                    >
                        {loading ? 'Checking...' : 'Refresh Status'}
                    </button>
                </div>

                <Footer />
            </div>
        </>
    );
}

export default Status;
